const baseUrl = process.env.REACT_APP_SERVER_URI ?? 'http://localhost:4000';
const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
const AUTH_STORAGE_KEY = 'pninit-user-session';

const buildUrl = (path) => `${normalizedBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;

const getStoredSessionToken = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    return (
      JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) || 'null')?.session?.token || ''
    );
  } catch {
    return '';
  }
};

const buildAuthHeaders = () => {
  const token = getStoredSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getArticles = async (
  { page = 1, pageSize = 10, search = '', searchScope = 'title', lang } = {},
  signal
) => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (search.trim()) {
    params.set('search', search.trim());
    params.set('searchScope', searchScope);
  }
  if (lang) params.set('lang', lang);

  const response = await fetch(buildUrl(`/api/articles?${params}`), { signal });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error('Server responded with an error.');
  }

  return {
    articles: payload.data?.articles ?? [],
    totalPages: payload.data?.totalPages ?? 1,
    total: payload.data?.total ?? 0,
  };
};

export const getArticleById = async (id, signal) => {
  if (!id) throw new Error('Article identifier is required.');

  const response = await fetch(buildUrl(`/api/articles/${encodeURIComponent(id)}`), { signal });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error('Server responded with an error.');
  }

  return payload.data ?? null;
};

export const createArticle = async (articleData, imageFile, signal) => {
  if (!articleData?.title) throw new Error('Article title is required.');

  const formData = new FormData();
  formData.append('payload', JSON.stringify(articleData));
  if (imageFile) formData.append('image', imageFile);

  const response = await fetch(buildUrl('/api/articles'), {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: formData,
    signal,
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error('Server responded with an error.');
  }

  return payload.data ?? null;
};

export const updateArticle = async (id, articleData, imageFile, signal) => {
  if (!id) throw new Error('Article identifier is required.');

  const formData = new FormData();
  formData.append('payload', JSON.stringify(articleData));
  if (imageFile) formData.append('image', imageFile);

  const response = await fetch(buildUrl(`/api/articles/${encodeURIComponent(id)}`), {
    method: 'PUT',
    headers: buildAuthHeaders(),
    body: formData,
    signal,
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error('Server responded with an error.');
  }

  return payload.data ?? null;
};

export const deleteArticle = async (id, signal) => {
  if (!id) throw new Error('Article identifier is required.');

  const response = await fetch(buildUrl(`/api/articles/${encodeURIComponent(id)}`), {
    method: 'DELETE',
    headers: buildAuthHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const payload = await response.json();

  if (!payload.success) {
    throw new Error('Server responded with an error.');
  }

  return true;
};
